import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useKey } from 'react-use';
import { resolveTheme, panelStyle } from '../themes';
import type { Theme } from '../themes';
import { TOTAL_FRAMES } from '../../domain/constants';

interface TimelineProps {
  theme?: string | Theme;
  frame: number;
  onFrameChange: (frame: number) => void;
  onNext: () => void;
  onPrev: () => void;
  totalFrames?: number;
  cachedFrames?: number[];
}

export function Timeline({
  theme, frame, onFrameChange, onNext, onPrev,
  totalFrames = TOTAL_FRAMES, cachedFrames = [],
}: TimelineProps) {
  const T = resolveTheme(theme);
  const trackRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [hoverFrame, setHoverFrame] = useState<number | null>(null);
  const cachedSet = useMemo(() => new Set(cachedFrames), [cachedFrames]);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      onFrameChange(frame >= totalFrames - 1 ? 0 : frame + 1);
    }, 220);
    return () => clearInterval(id);
  }, [playing, frame, onFrameChange, totalFrames]);

  useKey(' ', (e) => { e.preventDefault(); setPlaying(p => !p); });
  useKey('[', () => onPrev());
  useKey(',', () => onPrev());
  useKey(']', () => onNext());
  useKey('.', () => onNext());
  useKey('Home', () => onFrameChange(0));
  useKey('End', () => onFrameChange(totalFrames - 1));

  const seek = (clientX: number) => {
    const r = trackRef.current!.getBoundingClientRect();
    const t = (clientX - r.left) / r.width;
    onFrameChange(Math.max(0, Math.min(totalFrames - 1, Math.round(t * (totalFrames - 1)))));
  };

  const onTrackDown = (e: React.PointerEvent) => {
    seek(e.clientX);
    const move = (ev: PointerEvent) => seek(ev.clientX);
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const btn = (ariaLabel: string, onClick: () => void, content: React.ReactNode) => (
    <button onClick={onClick} aria-label={ariaLabel} className="timeline-btn" style={{
      appearance: 'none', border: `1px solid ${T.border}`, background: 'transparent',
      color: T.text, font: 'inherit', cursor: 'pointer',
      borderRadius: T.radius,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{content}</button>
  );

  const ticks = [0, Math.floor(totalFrames * 0.25), Math.floor(totalFrames * 0.5),
                 Math.floor(totalFrames * 0.75), totalFrames - 1];

  return (
    <div
      role="slider"
      aria-valuemin={0}
      aria-valuemax={totalFrames - 1}
      aria-valuenow={frame}
      aria-label="Timeline frame selector"
      tabIndex={0}
      className="timeline"
      style={panelStyle(T, { padding: '14px 18px' })}
    >
      <div className="timeline-inner">
        <div className="timeline-controls">
          <div className="timeline-btn-group">
            {btn('Go to start', () => onFrameChange(0), '|◀')}
            {btn('Previous frame', onPrev, '◀')}
            {btn(playing ? 'Pause' : 'Play', () => setPlaying(p => !p), playing ? '❚❚' : '▶')}
            {btn('Next frame', onNext, '▶')}
            {btn('Go to end', () => onFrameChange(totalFrames - 1), '▶|')}
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 2,
            fontSize: 10, color: T.textDim, textTransform: 'uppercase', minWidth: 70,
            fontVariantNumeric: 'tabular-nums',
          }}>
            <span>Frame</span>
            <span style={{ color: T.text, fontSize: 14, fontWeight: T.headerWeight }}>
              {String(frame).padStart(2, '0')} / {totalFrames - 1}
            </span>
          </div>
        </div>
        <div
          className="timeline-track"
          ref={trackRef}
          onPointerDown={onTrackDown}
          onMouseMove={(e) => {
            const r = trackRef.current!.getBoundingClientRect();
            const t = (e.clientX - r.left) / r.width;
            setHoverFrame(Math.max(0, Math.min(totalFrames - 1, Math.round(t * (totalFrames - 1)))));
          }}
          onMouseLeave={() => setHoverFrame(null)}
          style={{ flex: 1, position: 'relative', height: 44, cursor: 'pointer', userSelect: 'none', touchAction: 'none', minWidth: 0 }}
        >
          <div style={{
            position: 'absolute', left: 0, right: 0, top: 14,
            display: 'grid', gridTemplateColumns: `repeat(${totalFrames}, 1fr)`, gap: 0,
          }}>
            {Array.from({ length: totalFrames }, (_, i) => (
              <div key={i} style={{
                height: 6,
                background: cachedSet.has(i) ? T.accent : 'rgba(127,127,127,.18)',
                opacity: cachedSet.has(i) ? 0.85 : 1,
                margin: '0 1px', borderRadius: 1,
              }} />
            ))}
          </div>
          <div style={{
            position: 'absolute', left: 0, right: 0, top: 26,
            display: 'flex', justifyContent: 'space-between',
            color: T.textDim, fontSize: 9.5, fontVariantNumeric: 'tabular-nums',
          }}>
            {ticks.map((n) => <span key={n}>{String(n).padStart(2, '0')}</span>)}
          </div>
          {hoverFrame !== null && hoverFrame !== frame && (
            <div style={{
              position: 'absolute', top: 0,
              left: `calc(${(hoverFrame / (totalFrames - 1)) * 100}% - 14px)`,
              width: 28, height: 8, marginTop: 4,
              border: `1px solid ${T.textDim}`, borderRadius: 1, pointerEvents: 'none',
            }} />
          )}
          <div style={{
            position: 'absolute', top: 0, bottom: 6,
            left: `calc(${(frame / (totalFrames - 1)) * 100}% - 1px)`,
            width: 2, background: T.accent2,
            boxShadow: `0 0 10px ${T.accent2}`, pointerEvents: 'none',
          }}>
            <div style={{
              position: 'absolute', top: -2, left: -5,
              width: 12, height: 12, background: T.accent2, borderRadius: '50%',
              boxShadow: `0 0 8px ${T.accent2}`,
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}
